import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { ProfileDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { Avatar, Coins, ErrorState, Icon, Loading, Modal, TopBar } from '../components/ui';
import { useFormat, useT } from '../i18n';
import { errorMessage, useAsync } from '../lib/hooks';
import { shareOrCopy } from '../lib/device';
import { useSession } from '../store/session';
import { toast } from '../store/ui';

const REASONS = ['CHEATING', 'ABUSE', 'SPAM', 'INAPPROPRIATE_NAME', 'COLLUSION', 'OTHER'] as const;

export function Profile() {
  const { id } = useParams();
  const t = useT();
  const f = useFormat();
  const nav = useNavigate();
  const me = useSession((s) => s.user)!;
  const refreshMe = useSession((s) => s.refreshMe);
  const showBotBadge = useSession((s) => (s.boot?.config.bots as { showBadge?: boolean } | undefined)?.showBadge ?? false);
  const userId = id ?? me.id;
  const isMe = userId === me.id;
  const profile = useAsync(() => api.get<ProfileDto & { online?: boolean }>(`/api/v1/users/${userId}`), [userId]);
  const history = useAsync(() => (isMe ? api.get<{ matchId: string; gameType: string; place: number | null; coinsDelta: number; startedAt: string }[]>('/api/v1/me/games') : Promise.resolve([])), [isMe]);
  const referral = useAsync(() => (isMe ? api.get<{ code: string; link: string; invited: number; rewarded: number; rewardPerFriend: number; requiredGames: number }>('/api/v1/referrals') : Promise.resolve(null)), [isMe]);
  const [report, setReport] = useState(false);
  const [reason, setReason] = useState<(typeof REASONS)[number]>('ABUSE');
  const [details, setDetails] = useState('');
  const [rename, setRename] = useState(false);
  const [newName, setNewName] = useState('');

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok, 'success');
      profile.reload();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  if (profile.loading) return <main className="page"><Loading /></main>;
  if (profile.error || !profile.data) return <main className="page"><ErrorState message={profile.error ?? undefined} onRetry={profile.reload} /></main>;
  const p = profile.data;
  const totals = p.stats.reduce((a, s) => ({ played: a.played + s.played, won: a.won + s.won }), { played: 0, won: 0 });

  return (
    <main className="page">
      <TopBar
        title={isMe ? t('profile.title') : p.username}
        onBack={isMe ? undefined : () => nav(-1)}
        right={isMe ? <Link to="/settings" className="icon-btn" aria-label={t('settings.title')}><Icon name="settings" /></Link> : undefined}
      />
      <section className="card pad-lg col" style={{ alignItems: 'center', textAlign: 'center' }}>
        <Avatar avatar={p.avatar} size={88} name={p.username} status={p.online ? 'online' : undefined} />
        <h2 style={{ margin: '6px 0 0' }}>{p.username}</h2>
        <div className="row" style={{ justifyContent: 'center' }}>
          <span className="pill accent">{t('common.level', { n: p.level })}</span>
          {p.isBot && showBotBadge && <span className="pill">{t('common.bot')}</span>}
          <span className="pill">Since {f.date(p.createdAt)}</span>
        </div>
        {isMe && (
          <div className="row wrap" style={{ justifyContent: 'center' }}>
            <button className="btn sm ghost" onClick={() => { setNewName(me.username); setRename(true); }}>{t('profile.editName')}</button>
            <Link className="btn sm ghost" to="/wallet">{t('profile.wallet')}</Link>
          </div>
        )}
        {!isMe && !p.isBot && (
          <div className="row wrap" style={{ justifyContent: 'center' }}>
            {p.friendship === 'NONE' && <button className="btn sm primary" onClick={() => void act(() => api.post('/api/v1/friends/requests', { userId }), t('friends.requestSent'))}>{t('friends.add')}</button>}
            {p.friendship === 'REQUEST_SENT' && <span className="pill">{t('friends.requestSent')}</span>}
            {p.friendship === 'FRIENDS' && <button className="btn sm ghost" onClick={() => void act(() => api.del(`/api/v1/friends/${userId}`), 'Removed')}>{t('friends.remove')}</button>}
            {p.friendship === 'BLOCKED' ? (
              <button className="btn sm ghost" onClick={() => void act(() => api.del(`/api/v1/blocks/${userId}`), 'Unblocked')}>{t('friends.unblock')}</button>
            ) : (
              <button className="btn sm ghost" onClick={() => void act(() => api.post('/api/v1/blocks', { userId }), 'Blocked')}>{t('friends.block')}</button>
            )}
            <button className="btn sm ghost" onClick={() => setReport(true)}><Icon name="flag" size={16} /> {t('friends.report')}</button>
          </div>
        )}
      </section>

      <h2 className="section-title">{t('profile.stats')}</h2>
      <div className="quick-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="quick-tile"><strong className="num" style={{ fontSize: 'var(--fs-xl)', color: 'var(--text)' }}>{totals.played}</strong>{t('profile.played')}</div>
        <div className="quick-tile"><strong className="num" style={{ fontSize: 'var(--fs-xl)', color: 'var(--text)' }}>{totals.won}</strong>{t('profile.won')}</div>
        <div className="quick-tile"><strong className="num" style={{ fontSize: 'var(--fs-xl)', color: 'var(--text)' }}>{totals.played ? Math.round((totals.won / totals.played) * 100) : 0}%</strong>{t('profile.winRate')}</div>
      </div>
      {p.stats.length > 0 && (
        <div className="list" style={{ marginTop: 12 }}>
          {p.stats.map((s) => {
            const r = p.ratings.find((x) => x.gameType === s.gameType);
            return (
              <div key={s.gameType} className="list-item">
                <strong className="grow">{t(`game.${s.gameType}`)}</strong>
                <span className="dim num">{s.won}/{s.played}</span>
                <span className="pill">{s.winRate}%</span>
                {r && <span className="pill accent" title={t('profile.rating')}>★ {r.rating}</span>}
              </div>
            );
          })}
        </div>
      )}

      <h2 className="section-title">{t('profile.achievements')}</h2>
      {p.achievements.length === 0 ? (
        <p className="dim">No achievements yet — play to unlock them!</p>
      ) : (
        <div className="row wrap">
          {p.achievements.map((a) => (
            <span key={a.code} className="pill gold" title={f.date(a.unlockedAt)}>🏅 {a.name}</span>
          ))}
        </div>
      )}

      {isMe && referral.data && (
        <>
          <h2 className="section-title">{t('profile.referral')}</h2>
          <div className="card col">
            <span className="dim">Friends who join with your code and play {referral.data.requiredGames} games earn you <Coins amount={referral.data.rewardPerFriend} size={16} />.</span>
            <div className="spread">
              <strong className="num" style={{ fontSize: 'var(--fs-xl)', letterSpacing: '0.15em' }}>{referral.data.code}</strong>
              <button className="btn sm primary" onClick={() => void shareOrCopy({ title: 'Card Hub', text: `Join me on Card Hub with code ${referral.data!.code}: ${referral.data!.link}`, url: referral.data!.link }).then((r) => r === 'copied' && toast(t('common.copied'), 'success'))}>
                <Icon name="share" size={16} /> {t('common.share')}
              </button>
            </div>
            <span className="muted">{referral.data.invited} invited · {referral.data.rewarded} rewarded</span>
          </div>
        </>
      )}

      {isMe && history.data && history.data.length > 0 && (
        <>
          <h2 className="section-title">{t('profile.history')}</h2>
          <div className="list">
            {history.data.map((h) => (
              <Link key={h.matchId} to={`/replay/${h.matchId}`} className="list-item" style={{ color: 'inherit' }} aria-label={`${t('replay.watch')}: ${t(`game.${h.gameType}` as 'game.CALL_BREAK')} ${f.relative(h.startedAt)}`}>
                <span className="pill">{t(`game.${h.gameType}` as 'game.CALL_BREAK')}</span>
                <span className="grow dim">{f.relative(h.startedAt)}</span>
                {h.place && <span className="pill gold">#{h.place}</span>}
                <Coins amount={h.coinsDelta} signed size={16} />
                <span aria-hidden="true" className="dim">▶</span>
              </Link>
            ))}
          </div>
        </>
      )}

      <Modal open={report} onClose={() => setReport(false)} title={t('report.title')}>
        <div className="col">
          <div className="field">
            <label htmlFor="rr">{t('report.reason')}</label>
            <select id="rr" className="input" value={reason} onChange={(e) => setReason(e.target.value as typeof reason)}>
              {REASONS.map((r) => (
                <option key={r} value={r}>{r.replace(/_/g, ' ').toLowerCase()}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="rd">{t('report.details')}</label>
            <textarea id="rd" className="input" rows={3} maxLength={1000} value={details} onChange={(e) => setDetails(e.target.value)} style={{ paddingTop: 10 }} />
          </div>
          <button className="btn danger block" onClick={() => void act(() => api.post('/api/v1/reports', { userId, reason, ...(details ? { details } : {}) }), t('report.thanks')).then(() => setReport(false))}>
            {t('report.submit')}
          </button>
        </div>
      </Modal>
      <Modal open={rename} onClose={() => setRename(false)} title={t('profile.editName')}>
        <form className="col" onSubmit={(e) => { e.preventDefault(); void act(() => api.patch('/api/v1/me', { username: newName }).then(() => refreshMe()), t('common.done')).then(() => setRename(false)); }}>
          <input className="input" value={newName} maxLength={20} onChange={(e) => setNewName(e.target.value.replace(/\s/g, ''))} aria-label={t('auth.username')} />
          <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>You can change your name once every 30 days.</span>
          <button className="btn primary block">{t('common.save')}</button>
        </form>
      </Modal>
    </main>
  );
}
